import { t } from '@lingui/core/macro';
import type { BrandInsightsServiceRecord } from '@navio/nsdk';
import { DashboardPieChart, NoddiCard, NoddiSkeleton } from 'noddi-ui-web';

import { translateSalesItemCategory } from '../../utils/translateBookingCategory';
import { formatCount, formatMoney, formatShare } from './brandInsightsFormat';

// The five chart tokens, then a neutral for the long tail; data-driven colors
// for the donut and its legend swatches.
const SLICE_COLORS = ['var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)'];
const OTHER_COLOR = 'var(--muted-foreground)';
const OTHER_KEY = 'other';

interface Props {
  /** Undefined while the period loads. */
  services?: BrandInsightsServiceRecord[];
}

interface ServiceSlice {
  key: string;
  name: string;
  color: string;
  bookedCount: number;
  revenue: BrandInsightsServiceRecord['revenue'];
}

export const BrandInsightsServices = ({ services }: Props) => (
  <NoddiCard title={t`Services`} description={t`What customers booked`}>
    {services ? <ServicesBreakdown services={services} /> : <ServicesSkeleton />}
  </NoddiCard>
);

const ServicesSkeleton = () => (
  <div className='flex flex-col items-center gap-5 md:flex-row'>
    <NoddiSkeleton className='size-48 shrink-0 rounded-full md:size-60' />
    <div className='flex w-full flex-col gap-3'>
      <NoddiSkeleton variant='button' className='h-6 w-full' />
      <NoddiSkeleton variant='button' className='h-6 w-full' />
      <NoddiSkeleton variant='button' className='h-6 w-full' />
      <NoddiSkeleton variant='button' className='h-6 w-full' />
    </div>
  </div>
);

const ServicesBreakdown = ({ services }: { services: BrandInsightsServiceRecord[] }) => {
  const slices = toSlices(services);
  const totalBooked = slices.reduce((sum, slice) => sum + slice.bookedCount, 0);
  const totalRevenue = slices.reduce((sum, slice) => sum + slice.revenue.amount, 0);
  const topSlice = slices[0];
  const topName = topSlice?.name ?? '';
  const topShare = topSlice ? formatShare(topSlice.bookedCount, totalBooked) : '';
  const topRevenueShare = topSlice ? formatShare(topSlice.revenue.amount, totalRevenue) : '';

  return slices.length === 0 ? (
    <p className='text-sm text-muted-foreground'>{t`No services booked in this period yet.`}</p>
  ) : (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-col items-center gap-5 md:flex-row'>
        <DashboardPieChart
          wrapInCard={false}
          hideLegend
          className='w-full md:w-60'
          centerCount={totalBooked}
          data={slices.map((slice) => ({
            name: slice.name,
            value: slice.key,
            count: slice.bookedCount,
            color: slice.color
          }))}
        />
        <ul className='flex w-full flex-col'>
          {slices.map((slice) => (
            <li
              key={slice.key}
              className='grid grid-cols-[auto_minmax(0,1fr)_auto_auto] items-center gap-4 border-b border-border py-2 text-sm last:border-b-0'
            >
              <span className='size-3 rounded-sm' style={{ backgroundColor: slice.color }} />
              <span className='truncate font-medium'>{slice.name}</span>
              <span className='text-muted-foreground tabular-nums'>
                {formatCount(slice.bookedCount)} · {formatMoney(slice.revenue)}
              </span>
              <span className='w-12 text-right font-semibold tabular-nums'>
                {formatShare(slice.bookedCount, totalBooked)}
              </span>
            </li>
          ))}
        </ul>
      </div>
      {topSlice && (
        <p className='rounded-md bg-muted p-4 text-sm'>
          {t`${topName} is the most booked service at ${topShare} of booked services and ${topRevenueShare} of service revenue.`}
        </p>
      )}
    </div>
  );
};

// Top five categories get their own slice; the rest fold into "Other" so the
// donut stays readable however many categories a brand sells.
const toSlices = (services: BrandInsightsServiceRecord[]): ServiceSlice[] => {
  const sorted = [...services].sort((a, b) => b.bookedCount - a.bookedCount);
  const top: ServiceSlice[] = sorted.slice(0, SLICE_COLORS.length).map((service, index) => ({
    key: service.category,
    name: translateSalesItemCategory(service.category),
    color: SLICE_COLORS[index] ?? OTHER_COLOR,
    bookedCount: service.bookedCount,
    revenue: service.revenue
  }));
  const rest = sorted.slice(SLICE_COLORS.length);
  const [firstOfRest] = rest;
  if (!firstOfRest) {
    return top;
  }
  return [
    ...top,
    {
      key: OTHER_KEY,
      name: t`Other`,
      color: OTHER_COLOR,
      bookedCount: rest.reduce((sum, service) => sum + service.bookedCount, 0),
      revenue: {
        ...firstOfRest.revenue,
        amount: rest.reduce((sum, service) => sum + service.revenue.amount, 0)
      }
    }
  ];
};
